import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useRef, useState } from 'react';
import { useMutation } from 'urql';

import { useIsKinde } from 'containers/Auth/AuthWrapper';
import { MaybeCreateOrganization } from 'components/common/Askable/Providers/data/MaybeCreateOrganization.mutation';
import { ExchangeImpersonationToken } from 'src/hooks/data/ExchangeImpersonationToken.mutation';
import { UpdateMe } from 'data/mutations/user/updateMe.mutation';
import { isImpersonating, setImpersonationToken } from 'utils/impersonation';

import { useHandleCombinedError } from './useHandleCombinedError';

import type { ClientDetails } from 'context/ConnectedClientContext';

type Args = {
  updateClientDetails: (details: Partial<ClientDetails>) => void;
};

export function useSwitchTeam({ updateClientDetails }: Args) {
  const isKinde = useIsKinde();
  const { login } = useKindeAuth();
  const [switchingTeams, setSwitchingTeams] = useState(false);
  const [{ fetching, error }, triggerSwitchTeam] = useMutation(MaybeCreateOrganization);

  const { reuse } = useHandleCombinedError(error);
  const [, exchangeToken] = useMutation(ExchangeImpersonationToken);
  const [, updateUser] = useMutation(UpdateMe);

  // Prevent concurrent switchTeam calls (e.g. from re-renders firing the same auto-switch)
  const isSwitchingRef = useRef(false);

  const switchTeam = async (teamId: string, redirectUrl?: string) => {
    if (isSwitchingRef.current) return;
    isSwitchingRef.current = true;
    setSwitchingTeams(true);
    try {
      // Impersonation: exchange for a team-scoped token and reload
      if (isImpersonating()) {
        const { data: exchangeData } = await exchangeToken({ teamId });

        const token = exchangeData?.exchangeImpersonationToken?.token;
        if (!token) {
          throw new Error('Failed to exchange impersonation token');
        }

        setImpersonationToken(token);
        // Full page reload to re-bootstrap with new token (consistent with Kinde's full redirect)
        window.location.href = redirectUrl ?? '/';
        // Navigation triggers page teardown, but reset the guard in case it doesn't
        isSwitchingRef.current = false;
        setSwitchingTeams(false);
        return 'impersonation_switch';
      }

      const { data, error: triggerSwitchTeamError } = await triggerSwitchTeam({
        id: teamId,
      });

      if (triggerSwitchTeamError) {
        throw triggerSwitchTeamError;
      }

      if (isKinde) {
        const kindeOrgId = data?.maybeCreateKindeOrganization?.kinde_org_id;
        if (!kindeOrgId) {
          throw new Error('Failed to get Kinde organization ID');
        }
        void login({
          orgCode: kindeOrgId,
          ...(redirectUrl ? { state: { redirect_uri: redirectUrl } } : {}),
        });
        // Kinde triggers a full page redirect, but reset the guard in case it doesn't
        isSwitchingRef.current = false;
        setSwitchingTeams(false);
        return 'kinde_switch';
      }

      updateClientDetails({
        team: {
          id: data?.maybeCreateKindeOrganization?._id,
          name: data?.maybeCreateKindeOrganization?.name,
        },
      });
      // Update user in the db
      const result = await updateUser({
        user: {
          _default_team: data?.maybeCreateKindeOrganization?._id,
        },
      });
      if (result.error) {
        throw result.error;
      }
      isSwitchingRef.current = false;
      setSwitchingTeams(false);
      return result;
    } catch (e) {
      isSwitchingRef.current = false;
      setSwitchingTeams(false);
      reuse({ message: (e as { message?: string })?.message ?? 'Error switching team' });
    }
  };

  return {
    switchingTeams: switchingTeams || fetching,
    switchTeam,
  };
}
